import React, { useMemo } from 'react';
import TmSummary from '../../tm/tmsummary/TmSummary';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { fmtNum } from './monthlyCalculationUtils';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalHeaderSub, stdModalCloseBtn,
} from '../adminlayout/designStandard';

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    background: 'rgba(15,23,42,0.42)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: 'max(10px, env(safe-area-inset-top)) 12px max(10px, env(safe-area-inset-bottom))',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  modal: {
    width: 'min(1120px, calc(100vw - 24px))',
    maxHeight: 'min(92vh, calc(100dvh - 20px))',
    marginTop: 10,
    overflow: 'hidden',
    border: '1px solid var(--border)',
    borderRadius: 14,
    background: 'var(--bg-card)',
    boxShadow: '0 22px 70px rgba(15,23,42,0.28)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  // 모달 헤더 — 검정 표준 밴드 (2026-08-31 모달 통일)
  head: {
    ...stdModalHeaderBar(14),
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  headLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  iconBox: stdModalHeaderIconBox,
  title: { margin: 0, ...stdModalHeaderTitle },
  sub: stdModalHeaderSub,
  close: stdModalCloseBtn,
  body: { padding: 12, overflowY: 'auto', overflowX: 'hidden', minWidth: 0, boxSizing: 'border-box' },
  summaryWrap: { minWidth: 0, maxWidth: '100%', overflowX: 'hidden' },
  extra: {
    marginTop: 6,
    color: '#dc2626',
    fontSize: 11,
    fontWeight: 850,
    lineHeight: 1.45,
  },
};

function productOrder(name) {
  return 0;
}

function buildExcludedText(excludedItems, kpi) {
  const names = new Set();
  [...(kpi?.newItems || []), ...(kpi?.reorderItems || []), ...(excludedItems || [])].forEach((item) => {
    if (item.name) names.add(item.name);
  });
  const itemMap = new Map((excludedItems || []).map((item) => [item.name, Number(item.quantity || 0)]));
  return [...names]
    .sort((a, b) => productOrder(a) - productOrder(b) || a.localeCompare(b, 'ko'))
    .map((name) => `${name} ${fmtNum(itemMap.get(name) || 0)}장`)
    .join(', ');
}

function addExcludedItem(map, name, quantity, weightedQuantity) {
  const key = name || '미지정';
  if (!map.has(key)) map.set(key, { name: key, quantity: 0, weightedQuantity: 0 });
  const item = map.get(key);
  item.quantity += Math.abs(Number(quantity || 0));
  item.weightedQuantity += Math.abs(Number(weightedQuantity || 0));
}

function buildExcludedFromEvents(events = []) {
  const itemMap = new Map();
  let weightedQuantity = 0;

  for (const event of events || []) {
    for (const item of event.items || []) {
      const rawQty = Number(item.qty || 0);
      const countedQty = Number(item.countedQty || 0);
      const rawWeighted = Number(item.weightedQty ?? rawQty * Number(item.weight || 1));
      const countedWeighted = Number(item.countedWeightedQty ?? countedQty * Number(item.weight || 1));
      const diffWeighted = rawWeighted - countedWeighted;
      const diffQty = rawQty - countedQty;
      if (Math.abs(diffWeighted) <= 0.0001) continue;
      weightedQuantity += Math.abs(diffWeighted);
      addExcludedItem(itemMap, item.name, diffQty, diffWeighted);
    }
  }

  return {
    weightedQuantity: Math.round(weightedQuantity * 100) / 100,
    items: [...itemMap.values()]
      .map((item) => ({
        ...item,
        quantity: Math.round(item.quantity * 100) / 100,
        weightedQuantity: Math.round(item.weightedQuantity * 100) / 100,
      }))
      .sort((a, b) => productOrder(a.name) - productOrder(b.name) || a.name.localeCompare(b.name, 'ko')),
  };
}

function getExcludedBreakdown(row) {
  const eventExcluded = buildExcludedFromEvents(row.events || []);
  if (eventExcluded.weightedQuantity > 0) return eventExcluded;
  const legacyExcluded = row.reorderExcluded?.excluded || {};
  return {
    weightedQuantity: Number(legacyExcluded.weightedQuantity || 0),
    items: legacyExcluded.items || [],
  };
}

export function ConsultantMonthlySummaryModal({ row, year, month, onClose, scope = 'admin', chargeItemId = null, integratedMenuId = null }) {
  useBodyScrollLock(true);
  const companyCode = row.companyCode || 'PINE';
  const userId = row.consultantUserId;
  const queryBase = useMemo(() => {
    const params = new URLSearchParams({ company_code: companyCode });
    if (scope === 'admin' && integratedMenuId) params.set('integrated_menu_id', integratedMenuId);
    return params.toString();
  }, [companyCode, integratedMenuId, scope]);
  const basePath = scope === 'settlement' && chargeItemId
    ? `/api/my/settlement/consultant-charges/${chargeItemId}`
    : `/api/admin/marketer-consultant/consultants/${userId}`;

  const apiPaths = useMemo(() => ({
    summary: ({ dateRange }) => {
      const params = new URLSearchParams(queryBase);
      params.set('start_date', dateRange.start);
      params.set('end_date', dateRange.end);
      return `${basePath}/summary?${params.toString()}`;
    },
    calls: ({ year: y, month: m, feedback, dateRange }) => {
      const params = new URLSearchParams(queryBase);
      params.set('year', y);
      params.set('month', m);
      if (feedback) params.set('feedback', feedback);
      if (dateRange?.start) params.set('start_date', dateRange.start);
      if (dateRange?.end) params.set('end_date', dateRange.end);
      return `${basePath}/calls?${params.toString()}`;
    },
    kpi: ({ year: y, month: m }) => {
      const params = new URLSearchParams(queryBase);
      params.set('year', y);
      params.set('month', m);
      return `${basePath}/summary/kpi?${params.toString()}`;
    },
    calendarContracts: ({ year: y, month: m }) => {
      const params = new URLSearchParams(queryBase);
      params.set('year', y);
      params.set('month', m);
      return `${basePath}/calendar-contracts?${params.toString()}`;
    },
    // 월간 점수: 관리자 검증용으로만 노출.
    //   마케터 정산서(scope='settlement')는 마케터가 남(상담자)의 실적을 보는 화면이고
    //   화합·기타는 관리자 주관 평가라 동료에게 노출하지 않는다 → null 로 조회 자체를 막는다.
    score: scope === 'settlement' ? null : ({ year: y, month: m }) => {
      const params = new URLSearchParams({ company_code: companyCode });
      params.set('year', y);
      params.set('month', m);
      params.set('user_id', userId);
      return `/api/admin/tm-score/user?${params.toString()}`;
    },
  }), [basePath, queryBase, scope, companyCode, userId]);

  function renderTotalTicketsExtra({ totalTickets, kpiData, year: currentYear, month: currentMonth }) {
    if (Number(currentYear) !== Number(year) || Number(currentMonth) !== Number(month)) return null;
    const excluded = getExcludedBreakdown(row);
    const excludedWeighted = Number(excluded.weightedQuantity || 0);
    if (excludedWeighted <= 0) return null;
    const appliedTickets = Number(row.weightedCount ?? (Number(totalTickets || 0) - excludedWeighted));
    return (
      <div style={S.extra}>
        - 제외장수 {fmtNum(excludedWeighted)}장 ({buildExcludedText(excluded.items || [], kpiData)}) = 총 {fmtNum(appliedTickets)}장 적용
      </div>
    );
  }

  return (
    <div style={S.overlay} onMouseDown={onClose}>
      <div style={S.modal} onMouseDown={(event) => event.stopPropagation()}>
        <div style={S.head}>
          <div style={S.headLeft}>
            <div style={S.iconBox} aria-hidden="true">📅</div>
            <div style={{ minWidth: 0 }}>
              <h3 style={S.title}>{row.consultantName} {year}년 {month}월 요약</h3>
              <div style={S.sub}>{row.marketerName} 1:1 청구 검증용 · 직원페이지 요약 화면 공유</div>
            </div>
          </div>
          <button type="button" style={S.close} onClick={onClose} aria-label="닫기">✕</button>
        </div>
        <div style={S.body}>
          <div style={S.summaryWrap}>
            <TmSummary
              initialYear={year}
              initialMonth={month}
              title="요약"
              buildSummaryPath={apiPaths.summary}
              buildCallsPath={apiPaths.calls}
              buildKpiPath={apiPaths.kpi}
              buildCalendarContractsPath={apiPaths.calendarContracts}
              buildScorePath={apiPaths.score}
              renderTotalTicketsExtra={renderTotalTicketsExtra}
              lockedMonth
            />
          </div>
        </div>
      </div>
    </div>
  );
}
